import * as React from "react";
import { ChevronDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

/**
 * O ESPEC da tabela — não um datagrid.
 *
 * Isto veste o `<Table>` que já existe em `ui/table.tsx`; a página continua
 * escrevendo as suas linhas. O que o componente garante é que as 27 telas dos
 * arquétipos Índice, Cadastro e Relatório tenham a mesma tabela:
 *
 * - `th`: 36px, 11px em caixa alta com tracking, sobre `bg-muted/40`.
 * - `td`: 44px — que é também o alvo de toque no celular.
 * - **Número em `font-mono text-xs tabular-nums text-right`.** Todo número que
 *   se compara com outro número: use `DataTableNum`, não `DataTableCell`.
 * - A coluna que responde à pergunta da tela vai em `destaque`.
 *
 * **Largura por `<colgroup>`, nunca `width` no `<th>`.** Duas tabelas irmãs
 * dentro do mesmo `DataCard` — os grupos por dia da Irrigação — só alinham as
 * colunas se as duas declararem o MESMO `<colgroup>`. Com `width` no `th` cada
 * tabela se auto-dimensiona pelo próprio conteúdo e elas desalinham, que foi
 * exatamente o defeito daquela tela.
 */

export interface DataTableProps extends React.HTMLAttributes<HTMLTableElement> {
  /** Larguras das colunas, em ordem. `null` = coluna elástica. */
  colunas?: (string | null)[];
}

export function DataTable({ colunas, children, className, ...props }: DataTableProps) {
  return (
    // `table-fixed` só quando há `<colgroup>`: é ele que torna as larguras
    // declaradas autoritativas. Sem colgroup — matriz de N dias, como o
    // Financeiro — o layout automático é o certo, e forçar `table-fixed`
    // espremeria todas as colunas na mesma largura.
    <Table className={cn(colunas && "table-fixed", className)} {...props}>
      {colunas && (
        <colgroup>
          {colunas.map((largura, i) => (
            <col key={i} style={largura ? { width: largura } : undefined} />
          ))}
        </colgroup>
      )}
      {children}
    </Table>
  );
}

export const DataTableHeader = TableHeader;
export const DataTableBody = TableBody;

export function DataTableRow({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement>) {
  return <TableRow className={className} {...props} />;
}

export interface DataTableHeadProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** Cabeçalho de coluna numérica: alinha à direita, como os valores. */
  num?: boolean;
}

export function DataTableHead({ num, className, ...props }: DataTableHeadProps) {
  return (
    <TableHead
      className={cn(
        "h-9 px-3 bg-muted/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
        num && "text-right",
        className,
      )}
      {...props}
    />
  );
}

export interface DataTableCellProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** A coluna que responde à pergunta da tela. */
  destaque?: boolean;
}

export function DataTableCell({ destaque, className, ...props }: DataTableCellProps) {
  return (
    <TableCell
      className={cn(
        "h-11 px-3 py-1.5 text-sm",
        destaque && "font-semibold text-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** Célula numérica. É a única forma certa de mostrar número em tabela. */
export function DataTableNum({
  className,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <TableCell
      className={cn(
        "h-11 px-3 py-1.5 font-mono text-xs tabular-nums text-right",
        className,
      )}
      {...props}
    />
  );
}

export function DataTableFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <TableFooter className={cn("text-xs", className)} {...props} />;
}

/**
 * Linhas sem dado NÃO viram linhas de zero.
 *
 * Oito setores que não irrigaram no dia não são oito linhas de "0,0 m³" — isso
 * afirma medição onde não houve nenhuma, e enterra as linhas que importam. Elas
 * colapsam aqui: "8 setores sem irrigação · A4 · A5 …", clicável para abrir.
 */
export function LinhasVazias({
  quantidade,
  rotulo,
  amostra,
  colSpan,
  children,
}: {
  quantidade: number;
  /** "setores sem irrigação", "funcionários sem lançamento". */
  rotulo: string;
  /** Os primeiros nomes, para o resumo. */
  amostra?: string[];
  colSpan: number;
  /** As linhas de verdade, reveladas ao expandir. */
  children?: React.ReactNode;
}) {
  const [aberto, setAberto] = React.useState(false);
  if (quantidade === 0) return null;

  const resumo = amostra && amostra.length > 0 ? ` · ${amostra.slice(0, 4).join(" · ")}${amostra.length > 4 ? " …" : ""}` : "";

  return (
    <>
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={colSpan} className="h-9 px-3 py-1 text-xs text-muted-foreground">
          <button
            type="button"
            onClick={() => setAberto(v => !v)}
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
            data-testid="button-linhas-vazias"
          >
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", aberto && "rotate-180")} />
            {quantidade} {rotulo}
            {!aberto && resumo}
          </button>
        </TableCell>
      </TableRow>
      {aberto && children}
    </>
  );
}
